// レイヤー操作のオーケストレーション層（DuckDB ⇄ 描画オブジェクト/レジストリの橋渡し）。
//
// 役割: 「ファイル取込」「SQL からレイヤー生成」「GeoJSON 取得」「削除」「復元」といった
//       一連の DuckDB 操作をまとめ、layer-store.js が扱う描画オブジェクトを返す。
//       duckdb/ の各モジュール（loadFile / geo-normalize / toGeoJSON / layer-registry）を
//       束ねる薄い手続き層。
// 関係: Phase A は App / 手動 UI から、Phase B は tools/register-tools.js のツールハンドラから
//       共通で利用する（取込・生成のロジックを一本化して二重実装を防ぐ）。
import { query, getDb, getConnection } from '../duckdb/db'
import {
  loadFile,
  loadSqlAsTable,
  dropTable,
} from '../duckdb/loadFile'
import { normalizeTable, detectSourceCrs } from '../duckdb/geo-normalize'
import { buildRTreeIndexSql, shouldCreateRTreeIndex } from '../duckdb/spatial-sql'
import { tableToGeoJSON } from '../duckdb/toGeoJSON'
import { registerLayer, removeLayer, getLayer } from '../duckdb/layer-registry'
import { exportSpec } from './export-spec'
import { buildChoroplethStyle } from '../viz/choropleth'

// 大容量レイヤーの GeoJSON 全量変換を抑止する上限（plan.md §9）。
// 超過時は呼び出し側に警告を返し、簡略化や絞り込みを促す。
const MAX_GEOJSON_FEATURES = 50000

// ファイル/URL 取込レイヤーにだけ作る空間 index。SQL 派生レイヤーや描画レイヤーには張らない。
async function createRTreeIndexIfUseful({ table, geomColumn = 'geom', featureCount, enabled = true }) {
  if (!enabled || !shouldCreateRTreeIndex(featureCount)) return false
  try {
    await query(buildRTreeIndexSql(table, { geomColumn }))
    return true
  } catch {
    // Index は取込後の高速化用なので、作成失敗でレイヤー取込自体は失敗させない。
    return false
  }
}

// テーブルの代表的なジオメトリ型（最頻値）を返す。
async function detectGeomType(table, geomColumn = 'geom') {
  const rows = await query(
    `SELECT ST_GeometryType("${geomColumn}") AS t, count(*) AS c ` +
      `FROM "${table}" WHERE "${geomColumn}" IS NOT NULL GROUP BY t ORDER BY c DESC;`,
  )
  return rows[0]?.t ?? null
}

// レジストリ情報 + GeoJSON から描画オブジェクトを組み立てる。
async function buildLayerObject({
  name,
  geomColumn,
  featureCount,
  geomType,
  displayName,
  color,
  originPrompt = null,
  originSql = null,
  sourceLayers = [],
}) {
  const { featureCollection, bounds } = await tableToGeoJSON(name, geomColumn)
  return {
    layerId: name,
    displayName: displayName ?? name,
    featureCollection,
    color,
    visible: true,
    featureCount,
    geomType: geomType ?? null,
    bounds,
    originPrompt,
    originSql,
    sourceLayers,
    createdAt: new Date().toISOString(),
  }
}

// ファイルを取り込み、正規化・レジストリ登録まで行って描画オブジェクトを返す。
// CRS 不明時は normalizeTable が UnknownCrsError を投げる（UI 側で確認モーダル → declaredCrs 付きで再実行）。
// displayName 省略時はファイル名を表示名にする。
export async function importFile(
  file,
  { color, declaredCrs, displayName, createSpatialIndex = true } = {},
) {
  const raw = await loadFile(file)
  const sourceCrs = detectSourceCrs(file.name, declaredCrs)
  // sourceCrs が null なら UnknownCrsError。CRS 不明のまま処理しない（plan.md §5.5）。
  const norm = await normalizeTable({
    table: raw.name,
    geomColumn: raw.geomColumn,
    sourceCrs,
  })
  const spatialIndexCreated = await createRTreeIndexIfUseful({
    table: norm.name,
    geomColumn: norm.geomColumn,
    featureCount: norm.featureCount,
    enabled: createSpatialIndex,
  })
  const name = displayName ?? file.name
  await registerLayer({
    layerId: norm.name,
    displayName: name,
    geomType: norm.geomType,
    featureCount: norm.featureCount,
    sourceLayers: [],
    originPrompt: null,
    originSql: null,
  })
  const layer = await buildLayerObject({ ...norm, displayName: name, color })
  return { ...layer, spatialIndexCreated }
}

// 地図上で描いた GeoJSON（FeatureCollection）をレイヤー化する。
// 描画座標は経緯度（EPSG:4326）固定なので declaredCrs を明示し、CRS 確認モーダルには入れない。
// 取込パイプライン（importFile）に合流させ、正規化・レジストリ登録・描画を共通化する。
export async function importGeoJSON(featureCollection, { displayName = 'drawing', color } = {}) {
  const json = JSON.stringify(featureCollection)
  // .geojson 拡張子を付けて ST_Read に GeoJSON として認識させる。
  const file = new File([json], `${displayName}.geojson`, { type: 'application/geo+json' })
  return importFile(file, {
    color,
    declaredCrs: 'EPSG:4326',
    displayName,
    createSpatialIndex: false,
  })
}

// SELECT/WITH 文の結果をレイヤー化し、レジストリ登録して描画オブジェクトを返す。
// originPrompt / sourceLayers は来歴として記録する（Phase B のエージェント生成で活用）。
export async function addSqlLayer(
  sql,
  { color, displayName, originPrompt = null, sourceLayers = [], baseName = 'query' } = {},
) {
  const info = await loadSqlAsTable(sql, baseName)
  const geomType = await detectGeomType(info.name, info.geomColumn)
  await registerLayer({
    layerId: info.name,
    displayName: displayName ?? info.name,
    geomType,
    featureCount: info.featureCount,
    sourceLayers,
    originPrompt,
    originSql: sql,
  })
  return buildLayerObject({
    ...info,
    geomType,
    displayName,
    color,
    originPrompt,
    originSql: sql,
    sourceLayers,
  })
}

// レイヤー（テーブル + レジストリ行）を削除する。
export async function deleteLayer(layerId) {
  await dropTable(layerId)
  await removeLayer(layerId)
}

// 削除済みレイヤーを origin_sql から再生成する（undo 用、plan.md §6.3）。
// origin_sql を持たない取込レイヤーは元データが無いため復元できない。
export async function recreateLayer(layer) {
  if (!layer.originSql) {
    throw new Error('元データ（origin_sql）が無いため、このレイヤーは復元できません')
  }
  const trimmed = layer.originSql.trim().replace(/;\s*$/, '')
  await query(`CREATE TABLE "${layer.layerId}" AS ${trimmed};`)
  await registerLayer({
    layerId: layer.layerId,
    displayName: layer.displayName,
    geomType: layer.geomType,
    featureCount: layer.featureCount,
    sourceLayers: layer.sourceLayers,
    originPrompt: layer.originPrompt,
    originSql: layer.originSql,
  })
}

// ファイル名に使えない文字を除去する。
function safeFileName(name) {
  return (
    String(name ?? 'layer')
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '_')
      .slice(0, 100) || 'layer'
  )
}

// レイヤーを指定形式でエクスポートする（DOM 非依存。ダウンロードは UI 側で行う）。
// 戻り値: { filename, mime, data }（data は string か Uint8Array）
// レイヤーは 4326 保持なので 4326 で出力する。描画上限とは別に全件出力する。
export async function exportLayer(layerId, format, { geomColumn = 'geom' } = {}) {
  const { ext, mime } = exportSpec(format)
  const meta = await getLayer(layerId)
  const base = safeFileName(meta?.display_name || layerId)
  const filename = `${base}.${ext}`

  if (format === 'geoparquet') {
    const db = await getDb()
    await getConnection()
    // 仮想 FS へ COPY → バッファ取得 → 後始末。spatial ロード時は GeoParquet メタが書かれる。
    const virtualName = `export_${Date.now()}_${layerId}.parquet`
    await query(`COPY "${layerId}" TO '${virtualName}' (FORMAT PARQUET);`)
    const data = await db.copyFileToBuffer(virtualName)
    try {
      await db.dropFile(virtualName)
    } catch {
      // FS 後始末の失敗は無視
    }
    return { filename, mime, data }
  }

  // GeoJSON: 既存の変換を再利用（全件）。
  const { featureCollection } = await tableToGeoJSON(layerId, geomColumn)
  return { filename, mime, data: JSON.stringify(featureCollection) }
}

// 描画用 GeoJSON を上限つきで取得する（get_layer_geojson ツールの基盤）。
// 戻り値: { featureCollection, bounds, truncated, featureCount }
export async function getLayerGeoJSON(layerId, { geomColumn = 'geom' } = {}) {
  const meta = await getLayer(layerId)
  const featureCount = meta ? Number(meta.feature_count) : null
  if (featureCount != null && featureCount > MAX_GEOJSON_FEATURES) {
    // 上限超過。全量は返さず、呼び出し側で簡略化/絞り込みを促す。
    return { featureCollection: null, bounds: null, truncated: true, featureCount }
  }
  const { featureCollection, bounds } = await tableToGeoJSON(layerId, geomColumn)
  return { featureCollection, bounds, truncated: false, featureCount }
}

// テーブルの範囲（bbox）だけを軽量に取得する（全件 GeoJSON 化を避ける）。
async function getBounds(table, geomColumn = 'geom') {
  const g = `"${geomColumn}"`
  const [extent] = await query(
    `SELECT min(ST_XMin(${g})) AS minx, min(ST_YMin(${g})) AS miny, ` +
      `max(ST_XMax(${g})) AS maxx, max(ST_YMax(${g})) AS maxy FROM "${table}";`,
  )
  if (!extent || extent.minx == null || !Number.isFinite(Number(extent.minx))) return null
  return [
    [Number(extent.minx), Number(extent.miny)],
    [Number(extent.maxx), Number(extent.maxy)],
  ]
}

// レイヤーのスキーマ・ジオメトリ型・範囲・サンプル行を返す（describe_layer ツールの基盤）。
// SQL 生成の grounding 用。geom 本体は重いので返さず、型と範囲だけ示す。
export async function describeLayer(layerId, { geomColumn = 'geom', sampleRows = 3 } = {}) {
  const meta = await getLayer(layerId)
  if (!meta) throw new Error(`レイヤーが見つかりません: ${layerId}`)

  const cols = await query(`PRAGMA table_info('${layerId}');`)
  const columns = cols.map((c) => ({ name: c.name, type: c.type }))

  // 属性列名（geom 以外）。描画レイヤーのように geom しか無い場合に備える。
  const attrCols = cols.map((c) => c.name).filter((name) => name !== geomColumn)

  const bounds = await getBounds(layerId, geomColumn)

  // 属性列のサンプル（型確認に足る数行）。属性列が無い（geom のみ）なら空サンプルを返す。
  // ※ `SELECT * EXCLUDE(geom)` は属性列ゼロだと "SELECT list is empty" で失敗するため使わない。
  const limit = Number(sampleRows) || 3
  const sample =
    attrCols.length === 0
      ? []
      : await query(
          `SELECT ${attrCols.map((n) => `"${n}"`).join(', ')} FROM "${layerId}" LIMIT ${limit};`,
        )

  return {
    layerId,
    displayName: meta.display_name,
    geomType: meta.geom_type,
    featureCount: Number(meta.feature_count),
    columns,
    bounds,
    sample,
  }
}

// DuckDB の数値型（コロプレスの段彩対象）を判定する型名パターン。
const NUMERIC_TYPE = /(INT|DECIMAL|DOUBLE|FLOAT|REAL|NUMERIC|HUGEINT)/i
// カテゴリ配色で扱う最大カテゴリ数（多すぎる名義値はプロンプト/凡例が膨れるため上限）。
const MAX_CATEGORIES = 12

// 指定レイヤーの 1 列からコロプレス用の style を算出する（set_layer_style ツールの基盤）。
// 列型から数値（段彩）/ カテゴリを自動判定（method で上書き可）。クラス分けの材料（breaks /
// categories）を DuckDB から求め、配色・凡例の組み立ては viz/choropleth（純関数）に委ねる。
// ストアは触らず style を返すだけ（描画反映は呼び出し側が setLayerStyle で行う）。
export async function computeChoroplethStyle(
  layerId,
  { column, method, classes = 5, scheme, opacity } = {},
) {
  const meta = await getLayer(layerId)
  if (!meta) throw new Error(`レイヤーが見つかりません: ${layerId}`)
  if (!column) throw new Error('fill_column を指定してください')

  const cols = await query(`PRAGMA table_info('${layerId}');`)
  const colInfo = cols.find((c) => c.name === column)
  if (!colInfo) {
    const names = cols.map((c) => c.name).filter((n) => n !== 'geom')
    throw new Error(`列が見つかりません: ${column}（利用可能: ${names.join(', ')}）`)
  }

  const isNumericType = NUMERIC_TYPE.test(String(colInfo.type))
  let mode
  if (method === 'categorical') mode = 'categorical'
  else if (method === 'quantile' || method === 'equal_interval') mode = 'numeric'
  else mode = isNumericType ? 'numeric' : 'categorical'

  const col = `"${String(column).replace(/"/g, '""')}"`
  const tbl = `"${layerId}"`

  if (mode === 'numeric' && !isNumericType) {
    throw new Error(
      `列「${column}」は数値型ではありません（${colInfo.type}）。method を categorical にしてください。`,
    )
  }

  if (mode === 'categorical') {
    // 出現頻度の高い順に上限まで列挙する。
    const rows = await query(
      `SELECT ${col} AS v, count(*) AS c FROM ${tbl} WHERE ${col} IS NOT NULL ` +
        `GROUP BY v ORDER BY c DESC, v LIMIT ${MAX_CATEGORIES};`,
    )
    const categories = rows.map((r) => String(r.v))
    if (!categories.length) throw new Error(`列に有効な値がありません: ${column}`)
    return buildChoroplethStyle({ column, mode, categories, scheme, opacity })
  }

  // 数値: クラス数 n（2〜9 に丸める）の内側閾値 n-1 個を求める。
  const n = Math.max(2, Math.min(9, Math.floor(classes) || 5))
  let breaks
  if (method === 'equal_interval') {
    const [mm] = await query(
      `SELECT min(${col}) AS lo, max(${col}) AS hi FROM ${tbl} WHERE ${col} IS NOT NULL;`,
    )
    const lo = Number(mm?.lo)
    const hi = Number(mm?.hi)
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
      throw new Error(`列に数値がありません: ${column}`)
    }
    breaks = Array.from({ length: n - 1 }, (_, i) => lo + ((hi - lo) * (i + 1)) / n)
  } else {
    // 既定: 等量分類（quantile）。quantile_cont は確率リストで内側分位を一括取得できる。
    const probs = Array.from({ length: n - 1 }, (_, i) => (i + 1) / n)
    const [row] = await query(
      `SELECT quantile_cont(${col}, [${probs.join(', ')}]) AS q FROM ${tbl} WHERE ${col} IS NOT NULL;`,
    )
    const q = row?.q
    if (!q || q.length === 0) throw new Error(`列に数値がありません: ${column}`)
    breaks = Array.from(q, (v) => Number(v))
  }
  return buildChoroplethStyle({ column, mode, breaks, scheme, opacity })
}

// URL から外部データを取り込みレイヤー化する（import_data ツールの基盤）。
// ブラウザの fetch で取得 → File 化 → importFile（正規化・登録まで）に委譲する。
// CORS で取得できない場合は fetch が失敗し、エラーがエージェントへ返る（plan.md §4.4/§10）。
export async function importUrl(url, { color, declaredCrs } = {}) {
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`URL の取得に失敗しました（HTTP ${res.status}）: ${url}`)
  }
  const buf = new Uint8Array(await res.arrayBuffer())
  const fileName = url.split('/').pop()?.split('?')[0] || 'remote_data'
  const file = new File([buf], fileName)
  return importFile(file, { color, declaredCrs })
}

// 既存（登録済み）レイヤーの描画オブジェクトを組み立てる（get_layer_geojson で再描画する用）。
// 上限超過時は featureCollection=null を返し、呼び出し側で簡略化を促す。
export async function loadRenderObject(layerId, { color } = {}) {
  const meta = await getLayer(layerId)
  if (!meta) throw new Error(`レイヤーが見つかりません: ${layerId}`)
  const { featureCollection, bounds, truncated, featureCount } = await getLayerGeoJSON(layerId)
  return {
    layer: {
      layerId,
      displayName: meta.display_name,
      featureCollection,
      color,
      visible: true,
      featureCount,
      geomType: meta.geom_type,
      bounds,
      originPrompt: meta.origin_prompt ?? null,
      originSql: meta.origin_sql ?? null,
      sourceLayers: meta.source_layers ?? [],
      createdAt: meta.created_at ?? null,
    },
    truncated,
  }
}

export { MAX_GEOJSON_FEATURES }
